import { Component, Input, ChangeDetectionStrategy } from '@angular/core';

/** which corner of the icon the badge count sits on */
export enum BadgePosition {
  TopRight = 'top-right',
  TopLeft = 'top-left',
  BottomRight = 'bottom-right',
  BottomLeft = 'bottom-left',
}

@Component({
  selector: 'cs-icon-button',
  templateUrl: 'cs-icon-button.html',
  host: { 'class': 'icon-button' },
  standalone: false,
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class CsIconButton {
  /**
   * Icon name to display
   */
  @Input() icon: string = 'close';

  /**
   * The name a screen reader reads for this button. Bound onto the inner `<button>` rather
   * than left on the host, which is a plain custom element with no ARIA role — an
   * `aria-label` there is ignored by most screen readers. Every button here is icon-only, so
   * without this the control has no name at all.
   */
  @Input() ariaLabel?: string;

  /**
   * Whether to use filled variant of the icon
   */
  @Input() fill: boolean = false;

  /**
   * Badge count to display, shown once it reaches `badgeMinimum`
   */
  @Input() badge: number = null;
  /**
   * For a toggle: whether it is on, read out as `aria-pressed` on the inner button (ARIA on the
   * host is ignored). Left unset, the button is not a toggle and carries no `aria-pressed`.
   */
  @Input() pressed?: boolean;

  /**
   * The smallest count the badge shows. The default of 2 is the workout set bell's rule (one
   * message needs no number); the Add Foods Filters icon passes 1, since one filter on is
   * worth counting (#5902).
   */
  @Input() badgeMinimum: number = 2;

  /**
   * Which corner the badge sits on. Bottom right by default, where the workout set bell has
   * always had it; the Add Foods Filters icon puts it top right (#5902).
   */
  @Input() badgePosition: BadgePosition = BadgePosition.BottomRight;
}
